// src/pages/Matcher.tsx
import React, { FC, useEffect, useState, KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { TextField, Box, Chip, Tooltip } from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { RoutePaths } from 'routes/paths'
import { componentPermissions } from 'config/permissions'
import { componentNames } from 'config/roles'
import { sagaGetSingleEntityMatchAction } from 'saga/actions/matcher'
import { matcherSingleCustomInputSelector } from 'store/slices/matcherSingleCustomInputSlice'
import ContentBodyWrapper from 'components/custom/ContentBodyWrapper'
import PaperWrapper from 'components/custom/PaperWrapper'
import ButtonWrapper from 'components/custom/ButtonWrapper'
import { eButtonTypes, eButtonVariant, eColor } from 'components/types'


const getMuiThemeColor = (value: string): 'primary' | 'warning' | 'error' => {
  const normalized = value.toLowerCase().replace(/[^a-z]/g, '')
  if (['good', 'high', 'suitable', 'excellent'].some((k) => normalized.includes(k))) {
    return 'primary'
  }
  if (['medium', 'average', 'fair', 'partial'].some((k) => normalized.includes(k))) {
    return 'warning'
  }
  return 'error'
}

interface DataRowProps {
  label: string
  value?: string | null
  className?: string
}

const DataRow: FC<DataRowProps> = ({ label, value, className = '' }) => {
  if (!value) return null
  return (
    <div className={`flex flex-col gap-0.5 py-2 border-b border-slate-200 last:border-0 ${className}`}>
      <span className="text-xs font-bold uppercase tracking-widest text-slate-900">{label}</span>
      <span className="text-sm font-medium text-black leading-snug break-words">{value}</span>
    </div>
  )
}

const Matcher: FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const result = useSelector(matcherSingleCustomInputSelector)
  const [loading, setLoading] = useState(false)
  const [input, setInput] = useState('')
  const [isAdvancedMode, setIsAdvancedMode] = useState(false)

  const handleViewDetails = (sid: string, uuid: string | undefined, contextName?: string | null) => {
  const isStandardParts = contextName === '.standardParts'

  if (isStandardParts) {
      const url = `${RoutePaths.toolboxStandardPartsDetails.replace(':sid', sid)}?countryCode=OECD1&variant=var1`
      navigate(url)
    } else {
      const base = RoutePaths.singleEntity.replace(':id', uuid ?? sid)
      const url = contextName ? `${base}?contextName=${encodeURIComponent(contextName)}` : base
      navigate(url)
    }
  }

  const doSearch = () => {
    if (loading || !input.trim()) return
    setLoading(true)
    dispatch(sagaGetSingleEntityMatchAction(input.trim(), isAdvancedMode))
  }

  useEffect(() => {
    if (result) setLoading(false)
  }, [result])

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      doSearch()
    }
  }

  return (
    <ContentBodyWrapper title={t('matcher.title', 'Entity Matcher')}>
      <PaperWrapper>
        {/* ── Search Bar ── */}
        <div className="flex flex-col md:flex-row items-stretch md:items-center gap-4 mb-8 p-4 rounded-xl border-2 border-slate-300 bg-slate-50 shadow-sm">
          
          <div className="flex-1 min-w-[250px]">
            <TextField
              label={t('matcher.placeholder', 'Type an entity…')}
              placeholder={t('matcher.placeholder', 'Type an entity…')}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              variant="outlined"
              fullWidth
            />
          </div>

          <div className="flex flex-wrap items-center gap-4">
            {/* Wrapped the label in the MUI Tooltip */}
            <Tooltip 
              title={t('matcher.advancedSearchTooltip', 'Enable this to perform additional validation, ensuring higher confidence and more reliable match results.')}
              placement="top"
              arrow
            >
              <label className="flex items-center gap-3 cursor-pointer group select-none">
                <Box 
                  className="relative flex items-center justify-center w-6 h-6 bg-white rounded-md border-2 border-slate-300 transition-colors"
                  sx={{
                    '&:hover': {
                      borderColor: 'primary.main'
                    },
                    '.group:hover &': {
                      borderColor: 'primary.main'
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    className="peer absolute w-full h-full opacity-0 cursor-pointer"
                    checked={isAdvancedMode}
                    onChange={(e) => setIsAdvancedMode(e.target.checked)}
                  />

                  <Box 
                    className="pointer-events-none absolute opacity-0 peer-checked:opacity-100 w-full h-full rounded-[4px] flex items-center justify-center transition-all duration-200"
                    sx={{ bgcolor: 'primary.main' }}
                  >
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </Box>
                </Box>
                <span className="text-sm font-bold uppercase tracking-wider text-slate-600 group-hover:text-slate-900 transition-colors whitespace-nowrap">
                  {t('matcher.advancedSearch', 'Advanced Search')}
                </span>
              </label>
            </Tooltip>

            <ButtonWrapper
              onClick={doSearch}
              color={eColor.primary}
              disabled={loading || !input.trim()}
              type={eButtonTypes.button}
            >
              {t('matcher.search', 'Search')}
            </ButtonWrapper>
          </div>
        </div>

        {/* ── Loading State ── */}
        {loading && (
          <Box 
            className="flex items-center gap-3 p-5 rounded-xl border-2 shadow-sm"
            sx={{ borderColor: 'primary.main', bgcolor: 'transparent' }}
          >
            <Box
              className="w-5 h-5 rounded-full border-4 border-t-transparent animate-spin flex-shrink-0"
              sx={{ borderColor: 'primary.main', borderTopColor: 'transparent' }}
            />
            <Box component="p" sx={{ color: 'primary.main' }} className="text-sm font-semibold">
              {t('matcher.loading', 'Calculating the most probable match…')}
            </Box>
          </Box>
        )}

        {/* ── Empty State ── */}
        {!loading && !result && (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <svg
              className="w-14 h-14 text-slate-300"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
              />
            </svg>
            <p className="text-base font-semibold text-black">
              {t('matcher.help', 'Enter a term and press Enter or click Search.')}
            </p>
          </div>
        )}

        {/* ── Results ── */}
        {!loading && result && (
          <div className="space-y-6">

            {/* Query Summary Banner */}
            <Box 
              className="rounded-xl border-2 p-5 shadow-sm bg-white"
              sx={{ borderColor: 'primary.main' }}
            >
              <Box component="p" sx={{ color: 'primary.main' }} className="text-xs font-bold uppercase tracking-widest mb-1">
                {t('matcher.requested', 'You searched for')}
              </Box>
              <p className="text-xl font-bold text-black mb-4">
                {result.data.requestedEntity.customInput}
              </p>
              <Box component="p" sx={{ color: 'primary.main' }} className="text-xs font-bold uppercase tracking-widest mb-1">
                {t('matcher.contextResolved', 'Resolved Context String')}
              </Box>
              <p className="text-base font-medium text-black">
                {result.data.requestedEntity.assignedContextString}
              </p>
            </Box>

            {/* Result Cards */}
            {result.data.matchedResults?.length ? (
              <div className="space-y-6">
                {result.data.matchedResults.map((match, idx) => {
                  const vr = match.suggestionDetails.validationResults
                  
                  return (
                    <PaperWrapper key={match.suggestionId} className="p-0 overflow-hidden rounded-2xl border-2 border-slate-300 shadow-md">
                      

                      <Box 
                        className="flex flex-col lg:flex-row lg:items-start justify-between gap-5 px-6 py-4"
                        sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}
                      >
                        
                        <div className="flex items-start gap-3 w-full lg:w-auto">
                          <Box 
                            className="inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-extrabold flex-shrink-0 mt-0.5"
                            sx={{ bgcolor: 'background.paper', color: 'primary.main' }}
                          >
                            {idx + 1}
                          </Box>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-bold uppercase tracking-widest opacity-80 mb-0.5">
                              Suggestion ID
                            </p>
                            <p className="text-sm font-mono font-semibold break-all">
                              {match.suggestionId}
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 lg:justify-end">
                          
                          <div className="flex flex-col items-start lg:items-end">
                            <span className="text-xs font-bold uppercase tracking-widest opacity-80 mb-0.5">
                              Similarity
                            </span>
                            <span className="text-lg font-extrabold font-mono leading-none">
                              {(match.suggestionDetails.similarityScore * 100).toFixed(1)}
                              <span className="text-xs font-bold ml-0.5">%</span>
                            </span>
                          </div>
                          
                          <div className="flex flex-col items-start">
                            <span className="text-xs font-bold uppercase tracking-widest opacity-80 mb-1">
                              Confidence
                            </span>

                            <Chip 
                              label={match.suggestionDetails.confidenceLevel} 
                              color={getMuiThemeColor(match.suggestionDetails.confidenceLevel)} 
                              size="small" 
                              sx={{ fontWeight: 'bold' }} 
                            />
                          </div>
                          
                          {vr?.suitability && (
                            <div className="flex flex-col items-start">
                              <span className="text-xs font-bold uppercase tracking-widest opacity-80 mb-1">
                                Suitability
                              </span>
                              <Chip 
                                label={vr.suitability} 
                                color={getMuiThemeColor(vr.suitability)} 
                                size="small" 
                                sx={{ fontWeight: 'bold' }} 
                              />
                            </div>
                          )}
                        </div>
                      </Box>

                      {/* Card Body Grid */}
                      <div className="p-6 grid grid-cols-1 md:grid-cols-2 md:gap-x-8">
                        <Box 
                          className="flex items-end min-h-[2.5rem] text-xs font-extrabold uppercase tracking-widest text-black mb-3 pb-2 border-b-2 md:col-start-1 md:row-start-1"
                          sx={{ borderColor: 'primary.main' }}
                        >
                          Entity Profile
                        </Box>
                        
                        <DataRow className="md:col-start-1 md:row-start-2" label="SID" value={match.entity.sid} />
                        <DataRow className="md:col-start-1 md:row-start-3" label="Name" value={match.entity.name} />
                        <DataRow className="md:col-start-1 md:row-start-4" label="Context Name" value={match.entity.contextName} />
                        <DataRow className="md:col-start-1 md:row-start-5" label="Database" value={match.entity.database} />
                        <DataRow className="md:col-start-1 md:row-start-6" label="Type" value={match.entity.type} />
                        
                        {match.entity.description && (
                          <DataRow className="md:col-start-1 md:row-start-7" label="Description" value={match.entity.description} />
                        )}

                        <Box 
                          className="flex items-end min-h-[2.5rem] text-xs font-extrabold uppercase tracking-widest text-black mb-3 pb-2 border-b-2 mt-8 md:mt-0 md:col-start-2 md:row-start-1"
                          sx={{ borderColor: 'primary.main' }}
                        >
                          Classification Details
                        </Box>
                        
                        <DataRow className="md:col-start-2 md:row-start-2" label="Full Name" value={match?.entity?.fullContext?.fullName} />
                        <DataRow className="md:col-start-2 md:row-start-3" label="Short Name" value={match?.entity?.fullContext?.shortName} />
                        <DataRow className="md:col-start-2 md:row-start-4" label="Class Name" value={match?.entity?.fullContext?.className} />
                        <DataRow className="md:col-start-2 md:row-start-5" label="CPC" value={match?.entity?.fullContext?.centralProductClassification} />
                        <DataRow className="md:col-start-2 md:row-start-6" label="Context" value={match?.entity?.fullContext?.contextName} />
                        <DataRow className="md:col-start-2 md:row-start-7" label="Group" value={match?.entity?.fullContext?.group} />
                        <DataRow className="md:col-start-2 md:row-start-8" label="HS2017 Classification" value={match?.entity?.fullContext?.hs2017Classification} />

                      </div>

                      {/* Card Footer: Reasoning + View Details */}
                      <Box
                        className="p-6 bg-slate-50 border-t-2 flex flex-col sm:flex-row justify-between items-start sm:items-end gap-6 mt-auto"
                        sx={{ borderColor: 'primary.main' }}
                      >
                        <div className="flex-1">
                          {vr?.technicalReasoning && (
                            <>
                              <Box component="h4" sx={{ color: 'primary.main' }} className="text-xs font-extrabold uppercase tracking-widest mb-2">
                                Technical Reasoning
                              </Box>
                              <p className="text-sm font-medium text-black leading-relaxed italic">
                                {vr.technicalReasoning}
                              </p>
                            </>
                          )}
                        </div>
                        <div className="flex-shrink-0 w-full sm:w-auto mt-4 sm:mt-0">
                          <ButtonWrapper
                            //Hardcoded for testing
                            //onClick={() => handleViewDetails("s-cpd-13", "5e575765-cd96-4b75-a481-92bfed09e013", ".consumerProducts")}
                            //onClick={() => handleViewDetails("s-stp-1", "c67eb501-7814-47a3-bb67-a8bfead1d6e2", ".standardParts")}
                            onClick={() => handleViewDetails(match.entity.sid, match.entity.uuid, match.entity.contextName)}
                            color={eColor.primary}
                            variant={eButtonVariant.outlined}
                            type={eButtonTypes.button}
                            endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
                          >
                            {t('matcher.viewDetails', 'View Details')}
                          </ButtonWrapper>
                        </div>
                      </Box>
                    </PaperWrapper>
                  )
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-14 rounded-xl border-2 border-slate-300 bg-white">
                <p className="text-base font-bold text-black">No matches found.</p>
                <p className="text-sm font-medium text-slate-900 mt-1">
                  Try a different search term or use Advanced Search.
                </p>
              </div>
            )}
          </div>
        )}
      </PaperWrapper>
    </ContentBodyWrapper>
  )
}

export default {
  routeProps: {
    path: RoutePaths.toolboxMatcher,
    title: 'toolbox.matcher',
    element: <Matcher />,
    roles: componentPermissions[componentNames.pageMatcher],
    requiresAuthentication: true,
  },
  name: 'Matcher',
}